import React, { useEffect, useState } from "react";
import ConfirmationDialog from "../../confirmation-dialog/confirmation-dialog";
import { Tooltip, IconButton } from "@mui/material";
import { MoveRightIcon, Share2Icon, Edit2Icon, Trash2Icon, ChevronDownIcon, ChevronUpIcon } from "lucide-react";
import { FormGroup } from "../../models/form-group.model";
import { useAuthenticatedFetch } from "../../utils/auth-fetch";
import { FormModel } from "../../models/form.model";
import { CreateFormGroupDialog, FormGroupModel } from "./create-form-group-dialog";
import { useSnackbar } from "../../contexts/SnackbarContext";
import { useNavigate } from "react-router-dom";

function FormGroups() {
  const [formsData, setFormsData] = useState<FormModel[]>([]);
  const [formGroupData, setFormGroupData] = useState<FormGroup[]>([]);
  const [confirmDeleteFormGroupDialogOpen, setConfirmDeleteFormGroupDialogOpen] = useState(false);
  const [createFormGroupDialogOpen, setCreateFormGroupDialogOpen] = useState(false);
  const [formGroupForEditing, setFormGroupForEditing] = useState<FormGroupModel | undefined>(undefined);
  const [formGroupIdForDeletion, setFormGroupIdForDeletion] = useState<string>("");
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());


  const authenticatedFetch = useAuthenticatedFetch();
  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  useEffect(() => {
    getForms();
    getFormGroups();
  }, []);

  function getForms() {
    authenticatedFetch("/api/forms")
      .then((res) => res.json())
      .then((data) => {
        setFormsData(data["message"] === undefined ? data : []);
      })
      .catch((error) => console.error("Error fetching forms:", error));
  }

  const toggleDescription = (id: string) => {
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  function getFormGroups() {
    authenticatedFetch("/api/form-group")
      .then((res) => res.json())
      .then((data) => {
        setFormGroupData(data["message"] === undefined ? data : []);
      })
      .catch((error) => console.error("Error fetching form groups:", error));
  }

  const handleConfirm = async (confirmed: boolean) => {
    if (formGroupIdForDeletion === "") {
      return;
    }
    if (confirmed) {
      await handleDelete(formGroupIdForDeletion);
      setFormGroupIdForDeletion("");
    }
  };

  const handleShare = async (formGroupId: string) => {
    try {
        const response = await authenticatedFetch(`/api/form-group/${formGroupId}/share`,
            {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' }
            }
        );
        if (!response.ok) throw new Error('Failed to generate share URL');
        const { shareUrl } = await response.json();
        const fullUrl = `${window.location.origin}${shareUrl}`;
        setTimeout(() => {
          navigator.clipboard.writeText(fullUrl)
        }, 0)
        showSnackbar('Share URL copied to clipboard!', 'info');
    } catch (error) {
        showSnackbar('Error generating share URL', 'error');
    }
};

  const handleDelete = async (formId: string) => {
    try {
      const response = await authenticatedFetch(`/api/form-group/${formId}`, {
        method: 'DELETE'
      });
      if (!response.ok) throw new Error('Failed to delete form');
      getForms();
      showSnackbar('Form group deleted successfully!', 'success');
    } catch (error) {
      showSnackbar('Error deleting form', 'error');
    }
  };

  return (
    <div className="overflow-x-auto w-full">
      <button type="button" className="w-36 bg-orange-600 text-white py-2 m-2 px-4 rounded hover:bg-orange-700 float-end" onClick={() => setCreateFormGroupDialogOpen(true)}>
        + Create
      </button>
      <table className="min-w-full divide-y divide-gray-200">
        <thead>
          <tr>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              ID
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Title
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Description
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Form Count
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Created At
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              Updated At
            </th>
            <th scope="col" className="px-6 py-3 bg-gray-50 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
              Actions
            </th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {formGroupData.map((formGroup) => (
            <tr key={formGroup._id}>
              <td className="px-6 py-4 whitespace-nowrap">{formGroup._id}</td>
              <td className="px-6 py-4 whitespace-nowrap">{formGroup.title}</td>
              <td className="px-6 py-4">
                <div className="flex items-center space-x-2">
                  <div className={`${expandedDescriptions.has(formGroup._id) ? '' : 'max-w-xs truncate'}`}>
                    {formGroup.description}
                  </div>
                  <IconButton
                    onClick={() => toggleDescription(formGroup._id)}
                    className="h-6 w-6 hover:bg-gray-100 rounded"
                  >
                    {expandedDescriptions.has(formGroup._id) ? (
                      <ChevronUpIcon className="h-4 w-4" />
                    ) : (
                      <ChevronDownIcon className="h-4 w-4" />
                    )}
                  </IconButton>
                </div>
              </td>              <td className="px-6 py-4 whitespace-nowrap">{formGroup.forms.length}</td>
              <td className="px-6 py-4 whitespace-nowrap">{new Date(formGroup.createdAt).toLocaleString()}</td>
              <td className="px-6 py-4 whitespace-nowrap">{new Date(formGroup.updatedAt).toLocaleString()}</td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <Tooltip title="Open">
                  <IconButton onClick={() => navigate(`/forms/${formGroup.shareId}`)} aria-label="share">
                    <MoveRightIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Share">
                  <IconButton onClick={() => handleShare(formGroup._id)} aria-label="share">
                    <Share2Icon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    onClick={() => {
                      setFormGroupForEditing(formGroup as FormGroupModel);
                      setCreateFormGroupDialogOpen(true);
                    }}
                    aria-label="edit"
                  >
                    <Edit2Icon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    onClick={() => {
                      setConfirmDeleteFormGroupDialogOpen(true);
                      setFormGroupIdForDeletion(formGroup._id);
                    }}
                    aria-label="delete"
                  >
                    <Trash2Icon />
                  </IconButton>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <CreateFormGroupDialog
        open={createFormGroupDialogOpen}
        onClose={() => {
          setCreateFormGroupDialogOpen(false);
          setTimeout(() => {
            setFormGroupForEditing(undefined);
          }, 100);
          getFormGroups();
        }}
        forms={formsData}
        formGroup={formGroupForEditing}
      ></CreateFormGroupDialog>
      <ConfirmationDialog
        open={confirmDeleteFormGroupDialogOpen}
        onClose={() => {
          setConfirmDeleteFormGroupDialogOpen(false);
          setFormGroupIdForDeletion("");
        }}
        title="Are you sure?"
        description="This action cannot be undone."
        onConfirm={(event: boolean) => { handleConfirm(event); getFormGroups(); }}
      />
    </div>
  );
}

export default FormGroups;
